import { mulberry32, hashSeed } from './rng.js';

/**
 * @file Deterministic 2D noise. Value noise (not simplex) on purpose: it is
 * cheap, seedable per level with a real permutation table, and its blockiness
 * disappears under fbm at the frequencies we use (terrain cell 1.5m, amp 9m).
 *
 * CONVENTION: `Noise2D` samplers return values in [-1,1] (like simplex), so
 * `fbm2` is symmetric around 0 and `ridged2` can fold around the zero crossing.
 * Callers that want 0..1 do `n * 0.5 + 0.5`.
 */

/** @typedef {import('../types.js').Noise2D} Noise2D */

const PERM_SIZE = 512;
const MASK = 255;

/** Quintic fade — C2 continuous, no visible lattice creases. */
const fade = (t) => t * t * t * (t * (t * 6 - 15) + 10);

/**
 * Builds a seeded 2D value-noise sampler.
 * Feature size is 1 unit; scale your coordinates, not the noise.
 * @param {string|number} seed
 * @returns {Noise2D} `(x, y) => -1..1`
 */
export function makeValueNoise2D(seed) {
  const rng = mulberry32(hashSeed(seed));

  // Permutation table (deterministic Fisher-Yates), plus a value table so we do
  // one table lookup per corner instead of hashing at sample time.
  const perm = new Uint8Array(PERM_SIZE);
  const vals = new Float32Array(256);
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = p[i];
    p[i] = p[j];
    p[j] = t;
  }
  for (let i = 0; i < PERM_SIZE; i++) perm[i] = p[i & MASK];
  for (let i = 0; i < 256; i++) vals[i] = rng() * 2 - 1;

  const at = (xi, yi) => vals[perm[(xi + perm[yi & MASK]) & MASK]];

  return function noise2(x, y) {
    const xf = Math.floor(x);
    const yf = Math.floor(y);
    const tx = fade(x - xf);
    const ty = fade(y - yf);
    const x0 = xf & MASK;
    const y0 = yf & MASK;
    const x1 = (x0 + 1) & MASK;
    const y1 = (y0 + 1) & MASK;

    const v00 = at(x0, y0);
    const v10 = at(x1, y0);
    const v01 = at(x0, y1);
    const v11 = at(x1, y1);

    const a = v00 + (v10 - v00) * tx;
    const b = v01 + (v11 - v01) * tx;
    return a + (b - a) * ty;
  };
}

/**
 * Fractal brownian motion, normalised back into [-1,1].
 * @param {Noise2D} noise
 * @param {number} x
 * @param {number} y
 * @param {{oct?: number, lac?: number, gain?: number, freq?: number}} [opts]
 *   `oct` octaves, `lac` lacunarity (freq multiplier), `gain` amplitude decay,
 *   `freq` starting frequency.
 * @returns {number} -1..1
 */
export function fbm2(noise, x, y, opts = {}) {
  const { oct = 4, lac = 2, gain = 0.5, freq: freq0 = 1 } = opts;
  let amp = 1;
  let freq = freq0;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < oct; i++) {
    sum += amp * noise(x * freq, y * freq);
    norm += amp;
    amp *= gain;
    freq *= lac;
  }
  return norm > 0 ? sum / norm : 0;
}

/**
 * Ridged multifractal — folds each octave around zero to make crests. This is
 * the wasteland's dune/ridge generator.
 * @param {Noise2D} noise
 * @param {number} x
 * @param {number} y
 * @param {{oct?: number, lac?: number, gain?: number, freq?: number, sharp?: number}} [opts]
 *   `sharp` raises the ridge power (1 = soft, 2–3 = knife-edged).
 * @returns {number} 0..1, 1 at the ridge line.
 */
export function ridged2(noise, x, y, opts = {}) {
  const { oct = 4, lac = 2, gain = 0.5, freq: freq0 = 1, sharp = 1 } = opts;
  let amp = 1;
  let freq = freq0;
  let sum = 0;
  let norm = 0;
  for (let i = 0; i < oct; i++) {
    let n = 1 - Math.abs(noise(x * freq, y * freq));
    if (sharp !== 1) n = Math.pow(n, sharp);
    sum += amp * n;
    norm += amp;
    amp *= gain;
    freq *= lac;
  }
  return norm > 0 ? sum / norm : 0;
}

/**
 * Worley / cellular noise, F1 distance. Cell size is 1 unit.
 * Use for cracked ground, rust patches, rubble clustering, moss blotches.
 * @param {number} x
 * @param {number} y
 * @param {{seed?: string|number, jitter?: number}} [opts]
 * @returns {number} F1 distance, ~0 at a feature point, ~1 far from one.
 */
export function worley2(x, y, opts = {}) {
  return worley2Info(x, y, opts).f1;
}

/**
 * Full cellular result: nearest and second-nearest distance plus the owning
 * cell — `f2 - f1` draws the cell BORDERS (cracks), `id` gives per-cell random.
 * @param {number} x
 * @param {number} y
 * @param {{seed?: string|number, jitter?: number}} [opts]
 * @returns {{f1: number, f2: number, cx: number, cy: number, id: number}}
 */
export function worley2Info(x, y, opts = {}) {
  const { seed = 0, jitter = 1 } = opts;
  const s = hashSeed(seed);
  const xi = Math.floor(x);
  const yi = Math.floor(y);

  let f1 = Infinity;
  let f2 = Infinity;
  let cx = xi;
  let cy = yi;

  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      const gx = xi + ox;
      const gy = yi + oy;
      const h = cellHash(gx, gy, s);
      // Two independent 0..1 values out of one 32-bit hash.
      const px = gx + 0.5 + (((h >>> 8) & 0xffff) / 65535 - 0.5) * jitter;
      const py = gy + 0.5 + (((h >>> 20) & 0xfff) / 4095 - 0.5) * jitter;
      const dx = px - x;
      const dy = py - y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < f1) {
        f2 = f1;
        f1 = d;
        cx = gx;
        cy = gy;
      } else if (d < f2) {
        f2 = d;
      }
    }
  }
  return { f1, f2, cx, cy, id: cellHash(cx, cy, s) >>> 0 };
}

/**
 * @param {number} x
 * @param {number} y
 * @param {number} s
 * @returns {number} uint32
 */
function cellHash(x, y, s) {
  let h = (Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ s) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  h = Math.imul(h, 0x297a2d39);
  h ^= h >>> 15;
  return h >>> 0;
}
